import React, { createContext, useContext, useRef, useEffect, useCallback, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, set, onValue, remove, onDisconnect, serverTimestamp, get } from 'firebase/database';
import { LockOwner, UserLockedModal, TimeoutWarningModal, LoggedOutModal } from './LockModals';
import { useMattoState } from '../../MattoState';

export interface EditLockContextValue {
  canEdit: boolean;
  isLocked: boolean;
  lockOwner: LockOwner | null;
  requestEdit: () => Promise<void>;
  releaseLock: () => Promise<void>;
  forceReleaseLock: () => Promise<void>;
  isLoading: boolean;
}

const defaultContextValue: EditLockContextValue = {
  canEdit: false,
  isLocked: false,
  lockOwner: null,
  requestEdit: async () => {},
  releaseLock: async () => {},
  forceReleaseLock: async () => {},
  isLoading: true
};

const EditLockContext = createContext<EditLockContextValue>(defaultContextValue);

export const useEditLock = (): EditLockContextValue => {
  const context = useContext(EditLockContext);
  if (!context) {
    throw new Error('useEditLock must be used within EditLockProvider');
  }
  return context;
};

export interface EditLockProviderProps {
  children: ReactNode;
  projectId: string;
  currentUserId: string;
  currentUserName: string;
  lockTimeoutMs?: number;
  inactivityWarningMs?: number;
  shouldActivate?: boolean;
}

export const EditLockProvider: React.FC<EditLockProviderProps> = ({ 
  children, 
  projectId, 
  currentUserId, 
  currentUserName,
  lockTimeoutMs = 15 * 60 * 1000, // 15 minutes
  inactivityWarningMs = 14 * 60 * 1000, // 14 minutes warning
  shouldActivate = true
}) => {
  const navigate = useNavigate();
  const [canEdit, setCanEdit] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockOwner, setLockOwner] = useState<LockOwner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [showLoggedOutModal, setShowLoggedOutModal] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(60);

  // Refs for cleanup and timers
  const db = useRef(getDatabase());
  const lockRef = useRef(ref(db.current, `boardLocks/${projectId}`));
  const presenceRef = useRef(ref(db.current, `presence/${projectId}/${currentUserId}`));
  const activityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef(Date.now());
  const disconnectRef = useRef<any>(null);
  const autoRequestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isWaitingForLockRef = useRef(false);
  
  // ✅ Add ref to track current edit state for cleanup
  const canEditRef = useRef(false);

  // ✅ Update the ref whenever canEdit changes
  useEffect(() => {
    canEditRef.current = canEdit;
  }, [canEdit]);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Update activity in Realtime Database if we have the lock
    if (canEditRef.current && lockOwner?.userId === currentUserId) {
      set(lockRef.current, {
        userId: currentUserId,
        userName: currentUserName,
        timestamp: lockOwner.timestamp,
        lastActivity: Date.now()
      }).catch(console.error);
    }
  }, [lockOwner, currentUserId, currentUserName]);

  // Setup activity listeners
  useEffect(() => {
    if (!shouldActivate || !canEdit) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [shouldActivate, canEdit, updateActivity]);

  // Check if lock is stale
  const isLockStale = useCallback((lock: LockOwner | null): boolean => {
    if (!lock) return true;
    const timeSinceActivity = Date.now() - lock.lastActivity;
    return timeSinceActivity > lockTimeoutMs;
  }, [lockTimeoutMs]);

  // Request edit access
  const requestEdit = useCallback(async (skipLockCheck = false): Promise<void> => {
    if (!shouldActivate) return;
    
    setIsLoading(true);
    
    console.log('Requesting edit access...');

    try {
      // Check current lock status (unless skipping check for auto-acquisition)
      if (!skipLockCheck) {
        const snapshot = await get(lockRef.current);
        const currentLock = snapshot.val() as LockOwner | null;
        
        // If lock exists and isn't stale and isn't ours
        if (currentLock && !isLockStale(currentLock) && currentLock.userId !== currentUserId) {
          setShowModal(true);
          isWaitingForLockRef.current = true;
          setLockOwner(currentLock); // Set lock owner for modal display
          setIsLoading(false);
          return;
        }
      }
      
      // Acquire lock
      const lockData: LockOwner = {
        userId: currentUserId,
        userName: currentUserName,
        timestamp: Date.now(),
        lastActivity: Date.now()
      };
      
      await set(lockRef.current, lockData);
      
      // Set up presence
      await set(presenceRef.current, {
        timestamp: serverTimestamp()
      });
      
      // Set up automatic cleanup on disconnect
      disconnectRef.current = onDisconnect(lockRef.current);
      await disconnectRef.current.remove();
      
      // Also clean up presence on disconnect
      await onDisconnect(presenceRef.current).remove();
      
      setCanEdit(true);
      setLockOwner(lockData);
      isWaitingForLockRef.current = false; 

      // Start inactivity timer
      startInactivityTimer();
      
    } catch (error) {
      console.error('Failed to acquire lock:', error);
      isWaitingForLockRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [shouldActivate, currentUserId, currentUserName, isLockStale]);

  // Release lock - ✅ Use ref to check current state
  const releaseLock = useCallback(async (): Promise<void> => {
    if (!canEditRef.current) return;
    
    try {
      // Cancel onDisconnect
      if (disconnectRef.current) {
        await disconnectRef.current.cancel();
      }
      
      // Remove lock and presence
      await remove(lockRef.current);
      await remove(presenceRef.current);
      
      setCanEdit(false);
      setLockOwner(null);
      
      // Clear timers
      if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      
    } catch (error) {
      console.error('Failed to release lock:', error);
    }
  }, []); // ✅ Remove canEdit dependency since we use ref

  // Force release (for takeover scenarios)
  const forceReleaseLock = useCallback(async (): Promise<void> => {
    try {
      await remove(lockRef.current);
      await requestEdit(); // No skip check needed here - user manually requested
    } catch (error) {
      console.error('Failed to force release lock:', error);
    }
  }, [requestEdit]);

  // Start inactivity timer
  const startInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }
    
    // Start warning timer
    activityTimerRef.current = setTimeout(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      
      if (timeSinceActivity >= inactivityWarningMs) {
        // Show warning
        setShowTimeoutWarning(true);
        setTimeoutCountdown(60);
        
        // Start countdown
        countdownTimerRef.current = setInterval(() => {
          setTimeoutCountdown(prev => {
            if (prev <= 1) {
              // Time's up - log out
              handleTimeout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // Restart timer
        startInactivityTimer();
      }
    }, 30000); // Check every 30 seconds
  }, [inactivityWarningMs]);

  // Handle timeout
  const handleTimeout = useCallback(() => {
    setShowTimeoutWarning(false);
    setShowLoggedOutModal(true);
    releaseLock();
  }, [releaseLock]);

  // Continue working
  const handleContinueWorking = useCallback(async () => {
    setShowTimeoutWarning(false);
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    updateActivity();
    startInactivityTimer();
  }, [updateActivity, startInactivityTimer]);

  const saveBoard = useCallback(async () => {
    useMattoState.setState({ saveProjectNow: true });
    console.log('Saving board...');
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        clearInterval(interval);
        reject(new Error('Save timeout - taking too long'));
      }, 30000); // 30 second timeout
      
      const interval = setInterval(() => {
        const { isStateDirty } = useMattoState.getState();
        if (!isStateDirty) {
          clearInterval(interval);
          clearTimeout(timeout);
          console.log('Board saved successfully');
          resolve(true);
        }
      }, 1000);
    });
  }, []);

  const handleSaveAndLeave = useCallback(async () => {
    try {
      setShowTimeoutWarning(false);
      await saveBoard(); // Wait for save to complete
      releaseLock();
      navigate('/projects');
    } catch (error) {
      console.error('Failed to save board:', error);
    }
  }, [saveBoard, releaseLock, navigate]);
  
  const handleLeave = useCallback(() => {
    setShowTimeoutWarning(false);
    releaseLock();
    navigate('/projects');
  }, [releaseLock, navigate]);

  const handleLoggedOutOkay = useCallback(async () => {
    setShowLoggedOutModal(false);
    await saveBoard();
    navigate('/projects');
  }, [navigate]);

  // Listen for lock changes
  useEffect(() => {
    if (!shouldActivate) return;

    const unsubscribe = onValue(lockRef.current, (snapshot) => {
      const lock = snapshot.val() as LockOwner | null;
      const currentlyWaiting = isWaitingForLockRef.current;
      
      setIsLocked(!!lock);
      setLockOwner(lock);
      
      // Check if we lost the lock - ✅ Use ref for current state
      if (canEditRef.current && lock && lock.userId !== currentUserId) {
        setCanEdit(false);
        setShowModal(true);
        isWaitingForLockRef.current = true;
      }

      // Check if lock was just released and we were waiting
      if (!lock && currentlyWaiting) {
        // Lock was released and we were waiting for it
        setShowModal(false);
        isWaitingForLockRef.current = false;
        
        // Clear any existing timeout
        if (autoRequestTimeoutRef.current) {
          clearTimeout(autoRequestTimeoutRef.current);
          autoRequestTimeoutRef.current = null;
        }
        
        // Immediately try to acquire the lock - skip check since we know it was just released
        requestEdit(true).catch((error) => {
          console.error('Failed to auto-acquire lock:', error);
          // If immediate acquisition fails, show modal again
          setShowModal(true);
          isWaitingForLockRef.current = true;
        });
      }
      
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
      // Clean up any pending timeout
      if (autoRequestTimeoutRef.current) {
        clearTimeout(autoRequestTimeoutRef.current);
        autoRequestTimeoutRef.current = null;
      }
    };
    
  }, [shouldActivate, currentUserId, requestEdit]);

  // ✅ Cleanup on unmount - use ref to check current edit state
  useEffect(() => {
    return () => {
      if (canEditRef.current) {
        releaseLock();
      }
      // Clean up timeout on unmount
      if (autoRequestTimeoutRef.current) {
        clearTimeout(autoRequestTimeoutRef.current);
      }
    };
  }, [releaseLock]);

  const contextValue: EditLockContextValue = {
    canEdit,
    isLocked,
    lockOwner,
    requestEdit,
    releaseLock,
    forceReleaseLock,
    isLoading
  };

  return (
    <EditLockContext.Provider value={contextValue}>
      {children}
      
      <UserLockedModal
        open={showModal}
        shouldActivate={shouldActivate}
        lockOwner={lockOwner}
      />

      <TimeoutWarningModal
        open={showTimeoutWarning}
        shouldActivate={shouldActivate}
        timeoutCountdown={timeoutCountdown}
        onContinueWorking={handleContinueWorking}
        onSaveAndLeave={handleSaveAndLeave}
        onLeave={handleLeave}
      />

      <LoggedOutModal
        open={showLoggedOutModal}
        onOkay={handleLoggedOutOkay}
      />
    </EditLockContext.Provider>
  );
};